import React, { useRef } from 'react';
import { PDFExport } from '@progress/kendo-react-pdf';
// import { format } from 'date-fns';
import parse from 'html-react-parser';
import DomainQuizReport from './layout/DomainQuizReport';

interface SkillScore {
  skill: string;
  userScore: number;
  maxScore: number;
  percentage: number;
}

interface UserData {
  firstName?: string;
  lastName?: string;
  email?: string;
  domain?: string;
}

interface ProjectData {
  title?: string;
  domain?: string;
  completionDate?: string;
  projectOverview?: string;
  skillDescription?: string;
  nextSteps?: string;
}

interface Props {
  userData: UserData;
  projectData: ProjectData;
  skillScores: SkillScore[];
  domainData: any;
}

const SkillReportPDF: React.FC<Props> = ({
  userData,
  projectData,
  skillScores,
  domainData,
}) => {
  const domainQuizData = domainData[0];
  console.log(domainQuizData);
  const pdfExportComponent = useRef<PDFExport>(null);

  const generatePdf = () => {
    if (pdfExportComponent.current) {
      pdfExportComponent.current.save();
    }
  };

  interface SkillFeedback {
    meaning: string;
    recommendation: string;
  }

  const getSkillFeedback = (
    skillName: string,
    percentage: number
  ): SkillFeedback | null => {
    // First, create a mapping of skill names to their data ranges
    const skillMap: Record<string, { rangeStart: number }> = {
      'Critical Thinking and Problem-Solving': { rangeStart: 1 },
      'Communication Skills': { rangeStart: 8 },
      'Collaboration and Teamwork': { rangeStart: 15 },
      'Creativity and Innovation': { rangeStart: 22 },
      'Self-Management and Accountability': { rangeStart: 29 },
      'Leadership and Initiative': { rangeStart: 36 },
      'Emotional Intelligence and Empathy': { rangeStart: 43 },
      'Entrepreneurial Mindset': { rangeStart: 50 },
    };

    // Normalize the skill name to match our mapping
    const normalizedSkillName = skillName;

    // Find the skill in our mapping
    const skillInfo = skillMap[normalizedSkillName];

    if (!skillInfo) return null;

    // Get all score ranges for this skill
    const ranges: Array<{
      range: string;
      meaning: string;
      recommendation: string;
    }> = [];

    for (let i = 1; i <= 5; i++) {
      const row = skillInfo.rangeStart + i;
      const rangeKey = `A${row}` as keyof typeof SkillData;
      const meaningKey = `B${row}` as keyof typeof SkillData;
      const recommendationKey = `C${row}` as keyof typeof SkillData;

      // Type-safe access to SkillData
      const range = SkillData[rangeKey];
      const meaning = SkillData[meaningKey];
      const recommendation = SkillData[recommendationKey];

      if (
        typeof range === 'string' &&
        typeof meaning === 'string' &&
        typeof recommendation === 'string'
      ) {
        ranges.push({ range, meaning, recommendation });
      }
    }

    // Convert percentage to 0-1 scale for comparison
    const normalizedScore = percentage / 100;

    // Find the matching range
    for (const range of ranges) {
      const [minStr, maxStr] = range.range.split(' – ').map((s) => s.trim());
      const min = parseFloat(minStr);
      const max = parseFloat(maxStr);

      if (
        !isNaN(min) &&
        !isNaN(max) &&
        normalizedScore >= min &&
        normalizedScore <= max
      ) {
        return {
          meaning: range.meaning,
          recommendation: range.recommendation,
        };
      }
    }

    return null;
  };

  return (
    <div className='p-6 max-w-4xl mx-auto'>
      <PDFExport
        ref={pdfExportComponent}
        paperSize='A4'
        fileName={`${userData?.firstName || 'student'}-skill-report.pdf`}
        author={userData?.firstName || 'Belong'}
        subject='Skill Assessment Report'
        title={`${userData?.firstName || 'Student'} Skill Assessment Report`}
        scale={0.8}
        margin='1cm'>
        <div className='pdf-content'>
          <div
            className='relative h-[950px] bg-cover bg-center bg-no-repeat'
            style={{
              backgroundImage: `url('https://res.cloudinary.com/belong/image/upload/v1754653512/uploaded_resources/GSAP_new_report_yxfahn.jpg')`,
              backgroundSize: '100% 100%', // This will make the image cover the entire div without cropping
            }}>
            {/* Content container with centered alignment */}
            <div className='relative z-10 max-w-4xl mx-auto px-6 py-12'>
              <div className='rounded-lg p-8'>
                <h2 className='text-4xl font-bold mb-6 text-blue-700 border-b pb-2 mt-72'>
                  LEARNER ACHIEVEMENT SNAPSHOT
                </h2>
                <p className='mt-20 text-2xl '>
                  <strong>Name: {userData?.firstName}</strong>
                </p>
                <p className='mt-4 text-2xl '>
                  <strong>Email: {userData?.email || 'Not provided'}</strong>
                </p>
              </div>
            </div>
          </div>

          <div className='grid grid-cols-1 gap-6 mb-6'>
            <div className='bg-gray-50 p-4 rounded-lg shadow-sm'>
              <div className='space-y-2'>
                <h2 className='text-xl font-bold mt-6 mb-4 text-blue-600'>
                  Project Title:{' '}
                  {projectData?.title ||
                    'Interactive Web Redesign and Quiz Application'}
                </h2>
                <p>
                  <strong>Domain:</strong>{' '}
                  {userData?.domain || 'Domain Not Found'}
                </p>

                <p className='mt-4'>
                  You have worked on real-world industry projects, focusing on
                  self-upskilling and gaining hands-on experience in applying
                  technical skills to real-world challenges. This report
                  provides a personalized summary of your journey, highlighting
                  your project work, skill development, feedback, and
                  recommended next steps.
                </p>

                <div className='mt-6 mb-6'>
                  <h3 className='text-lg font-semibold mb-3 text-blue-600'>
                    Project Overview
                  </h3>
                  {projectData?.projectOverview ? (
                    <div className='prose max-w-none'>
                      {parse(projectData?.projectOverview || '')}
                    </div>
                  ) : (
                    <p>projectOverview not found</p>
                  )}
                </div>

                <div className='mb-6'>
                  <h3 className='text-lg font-semibold mb-3 text-blue-600'>
                    Skill Development
                  </h3>
                  {projectData?.skillDescription ? (
                    <div className='prose max-w-none'>
                      {parse(projectData?.skillDescription || '')}
                    </div>
                  ) : (
                    <p>Skill Development not found</p>
                  )}
                </div>
              </div>

              <div className='mb-6 mt-6'>
                <h3 className='text-lg font-semibold mb-3 text-blue-600'>
                  Aptitude Quiz Score
                </h3>
                <div className='bg-gray-100 p-4 mt-4 rounded'>
                  <div className='text-gray-700 font-semibold'>
                    <div>
                      <div className='mb-2 text-lg'>Overall Aptitude Score</div>
                      <div className='text-2xl font-bold text-primary mb-4'>
                        {Math.round(
                          skillScores.reduce(
                            (sum, skill) => sum + skill.percentage,
                            0
                          ) / skillScores.length
                        )}
                        %
                      </div>
                      <p className='text-sm mb-4'>
                        You show promising capabilities across core future-ready
                        skills.
                      </p>
                    </div>
                    <div className='mt-6 w-full'>
                      <div className='mb-2 text-md font-semibold'>
                        Below is your personalized feedback for each skill:
                      </div>
                      <div className='space-y-4'>
                        {skillScores.map((skill) => {
                          const feedback = getSkillFeedback(
                            skill.skill,
                            skill.percentage
                          );
                          return (
                            <div
                              key={skill.skill}
                              className='border-l-4 border-blue-500 pl-4 py-2 bg-white rounded-r'>
                              <h4 className='font-bold text-blue-700'>
                                {skill.skill} – {skill.percentage}%
                              </h4>
                              {feedback ? (
                                <>
                                  <p className='mt-1'>{feedback.meaning}</p>
                                  <p className='mt-2 text-sm italic'>
                                    <strong>Recommendation:</strong>{' '}
                                    {feedback.recommendation}
                                  </p>
                                </>
                              ) : (
                                <p className='mt-1'>
                                  No specific feedback available for this skill
                                  level.
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {domainQuizData && (
                <DomainQuizReport currentData={domainQuizData} />
              )}

              <div className='mb-6'>
                <h3 className='text-lg font-semibold mb-3 text-blue-600'>
                  Recommendations & Next Steps in Skill-Building
                </h3>
                {projectData?.nextSteps ? (
                  <div className='prose max-w-none'>
                    {parse(projectData.nextSteps)}
                  </div>
                ) : (
                  <div> not Found </div>
                )}
              </div>

              <div className='mb-6'>
                <h3 className='text-lg font-semibold mb-3 text-blue-600'>
                  Industry Project: Building the Foundation for Your Future in
                  Tech!
                </h3>
                <p>
                  Congratulations on successfully completing the Industry
                  Project! You've navigated real-world challenges, refined your
                  problem-solving and analytical skills, and gained hands-on
                  experience with industry-standard tools and methodologies.
                  This project wasn't just about theoretical knowledge, it was
                  about applying advanced concepts, leveraging data-driven
                  insights, and demonstrating your ability to drive impactful
                  solutions. Keep the momentum going: continue to innovate,
                  embrace new technologies, and leverage this experience as a
                  springboard for advanced career opportunities in your field.
                </p>
              </div>
            </div>
          </div>
        </div>
      </PDFExport>

      <div className='mt-6 flex justify-end pdf-button-container'>
        <button
          onClick={generatePdf}
          className='bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors'>
          Download as PDF
        </button>
      </div>
    </div>
  );
};

// Mock data structure for the Excel skill data
const SkillData = {
  // Critical Thinking & Problem Solving
  A1: 'Critical Thinking and Problem-Solving',
  A2: 'Score Range',
  A3: '0.00 – 0.24',
  A4: '0.25 – 0.49',
  A5: '0.50 – 0.74',
  A6: '0.75 – 0.89',
  A7: '0.90 – 1.00',
  B3: 'You struggle with breaking down problems or applying logic.',
  B4: 'You understand basic problems but may miss deeper patterns.',
  B5: 'You think clearly but may hesitate in unfamiliar problems.',
  B6: 'You solve problems using structured logic and some creativity.',
  B7: 'You analyze and solve complex problems independently.',
  C3: 'Start solving daily brainteasers or simple puzzles. Use apps like Lumosity or logic games. Ask "why" and "what if" in everyday situations.',
  C4: 'Practice with case studies or reasoning questions. Try discussing multiple approaches to the same problem.',
  C5: 'Join a school club like science or math olympiad. Reflect on how you solved problems and how else you could have.',
  C6: 'Take part in competitions that demand critical thinking (quizzes, debates, model UN). Explore alternate solutions regularly.',
  C7: 'Mentor peers in their problem-solving. Try participating in hackathons or entrepreneurial problem-based challenges.',

  // Communication Skills
  A8: 'Communication Skills',
  A9: 'Score Range',
  A10: '0.00 – 0.24',
  A11: '0.25 – 0.49',
  A12: '0.50 – 0.74',
  A13: '0.75 – 0.89',
  A14: '0.90 – 1.00',
  B10: 'You struggle to express thoughts clearly or confidently.',
  B11: 'You convey ideas, but clarity and listening may be limited.',
  B12: "You're clear but may not adapt communication to different people.",
  B13: 'You communicate effectively and are a good listener.',
  B14: 'You express ideas powerfully and adapt to different settings.',
  C10: 'Practice daily journaling and reading aloud. Record yourself explaining topics. Join speaking clubs.',
  C11: 'Focus on structuring thoughts before speaking. Listen to TED Talks to observe tone and flow.',
  C12: 'Engage in role-play (e.g. presenting to parents vs. peers). Practice summarizing topics to different audiences.',
  C13: 'Try blogging, podcasting, or leading discussions. Volunteer to anchor school events.',
  C14: 'Host storytelling sessions, debates, or mentor juniors. Consider speech competitions or creative writing forums.',

  // Continue with other skills in the same pattern...
  // Collaboration & Teamwork
  A15: 'Collaboration and Teamwork',
  A16: 'Score Range',
  A17: '0.00 – 0.24',
  A18: '0.25 – 0.49',
  A19: '0.50 – 0.74',
  A20: '0.75 – 0.89',
  A21: '0.90 – 1.00',
  B17: 'You find teamwork stressful or unclear.',
  B18: 'You participate but may struggle to balance listening and sharing.',
  B19: "You're reliable in teams but still growing in group dynamics.",
  B20: 'You contribute actively and respectfully.',
  B21: 'You are a collaborative force, empowering others.',
  C17: "Start with pairs or triads in low-stakes projects. Reflect after each group task—what worked, what didn't?",
  C18: "Rotate group roles—be a leader, note-taker, or facilitator. Learn from observing others' approaches.",
  C19: 'Work with new peers. Take initiative to resolve group disagreements positively.',
  C20: 'Join school clubs that require coordination (drama, robotics, community outreach).',
  C21: 'Lead project-based activities or interschool teams. Reflect on how to make teams inclusive and effective.',

  // Creativity & Innovation
  A22: 'Creativity and Innovation',
  A23: 'Score Range',
  A24: '0.00 – 0.24',
  A25: '0.25 – 0.49',
  A26: '0.50 – 0.74',
  A27: '0.75 – 0.89',
  A28: '0.90 – 1.00',
  B24: 'You stick to known paths, hesitant to explore.',
  B25: 'You generate ideas but may need a push to act on them.',
  B26: 'You show creativity but may lack consistency or confidence.',
  B27: 'You regularly bring new perspectives and ideas.',
  B28: "You're highly original and driven to innovate.",
  C24: 'Engage in activities like doodling, storytelling, or idea mapping. Avoid judging your ideas too soon.',
  C25: 'Maintain a "crazy ideas" notebook. Explore DIY projects or remix common school assignments creatively.',
  C26: 'Participate in design challenges or science/art fairs. Build projects with "what else can I try?" attitude.',
  C27: 'Start a passion project—an app, comic, podcast, or YouTube channel. Solve small problems around you creatively.',
  C28: 'Join innovation cells or maker clubs. Enter contests like Smart India Hackathon or Toycathon. Create a portfolio of your work.',

  // Self-Management & Accountability
  A29: 'Self-Management and Accountability',
  A30: 'Score Range',
  A31: '0.00 – 0.24',
  A32: '0.25 – 0.49',
  A33: '0.50 – 0.74',
  A34: '0.75 – 0.89',
  A35: '0.90 – 1.00',
  B31: 'You find it hard to follow routines or take responsibility.',
  B32: 'You are aware but inconsistent with deadlines and habits.',
  B33: 'You mostly meet deadlines and reflect on your progress.',
  B34: 'You are organized, disciplined, and goal-driven.',
  B35: "You're highly self-regulated and consistent.",
  C31: 'Use simple trackers (stickers, planner apps) to build small daily habits. Reward your progress weekly.',
  C32: 'Create a weekly goal sheet. Use the Pomodoro technique (25-min focus, 5-min break).',
  C33: 'Set academic and personal goals each month. Review what helped you succeed or distracted you.',
  C34: 'Help friends build habits. Mentor peers in goal setting. Track your long-term academic journey.',
  C35: 'Maintain a reflective journal. Share your goal-setting system through a workshop or blog. Push yourself with long-term projects.',

  // Leadership & Initiative
  A36: 'Leadership and Initiative',
  A37: 'Score Range',
  A38: '0.00 – 0.24',
  A39: '0.25 – 0.49',
  A40: '0.50 – 0.74',
  A41: '0.75 – 0.89',
  A42: '0.90 – 1.00',
  B38: 'You may be hesitant to take responsibility or lead.',
  B39: 'You show potential but rarely step forward.',
  B40: 'You have leadership traits but are still growing.',
  B41: 'You motivate, guide, and take initiative.',
  B42: 'You are a confident and inspiring leader.',
  C38: 'Begin by volunteering in small group tasks. Learn to say, "I can help with that."',
  C39: 'Ask teachers or peers how you can take on more responsibility. Set a goal to initiate one project or idea.',
  C40: 'Lead a club activity or peer group. Focus on motivating others and resolving conflicts.',
  C41: 'Take lead roles in school fests, drives, or team-based competitions. Reflect on your impact.',
  C42: 'Launch your own school campaign, project, or startup idea. Learn servant leadership principles.',

  // Emotional Intelligence & Empathy
  A43: 'Emotional Intelligence and Empathy',
  A44: 'Score Range',
  A45: '0.00 – 0.24',
  A46: '0.25 – 0.49',
  A47: '0.50 – 0.74',
  A48: '0.75 – 0.89',
  A49: '0.90 – 1.00',
  B45: "You struggle to manage emotions or recognize others' feelings.",
  B46: 'You are learning emotional cues but may act impulsively.',
  B47: 'You show kindness and can usually regulate emotions.',
  B48: 'You are emotionally aware and supportive.',
  B49: 'You show deep emotional intelligence and compassion.',
  C45: 'Start a mood journal. Observe how emotions affect your actions. Watch stories that explore different perspectives.',
  C46: "Practice pausing before reacting. Role-play scenarios where you think from another's shoes.",
  C47: 'Volunteer in empathy-driven programs (clean-up drives, teaching juniors). Reflect after emotional events.',
  C48: 'Lead wellness or peer support groups. Help others handle tough conversations.',
  C49: 'Use this gift to influence school culture positively. Facilitate discussions on inclusion and well-being.',

  // Entrepreneurial Mindset
  A50: 'Entrepreneurial Mindset',
  A51: 'Score Range',
  A52: '0.00 – 0.24',
  A53: '0.25 – 0.49',
  A54: '0.50 – 0.74',
  A55: '0.75 – 0.89',
  A56: '0.90 – 1.00',
  B52: 'You prefer clear directions and avoid risk or new ideas.',
  B53: "You're curious but unsure how to start or act.",
  B54: 'You think like a creator and explore ideas actively.',
  B55: "You're proactive and seek opportunities.",
  B56: 'You embody an entrepreneurial spirit.',
  C52: 'Explore entrepreneur stories (e.g. Shark Tank India or Indian Teenpreneurs). Reflect on everyday problems you could solve.',
  C53: 'Try simulation games like BizWorld or create mock business plans for fun. Share an idea with a friend or teacher.',
  C54: 'Join entrepreneurship clubs or student fairs. Learn basic budgeting or marketing.',
  C55: 'Build something small—a podcast, newsletter, or service. Talk to real entrepreneurs.',
  C56: 'Launch a passion project or startup idea. Look for incubators or junior entrepreneur programs (like TYE or Atal Tinkering Labs).',
};

export default SkillReportPDF;
